import React, { useEffect } from 'react';
import { PODIUM_COLOURS, topThreeGroups } from '../utils/ranking';

const kf = `
  @keyframes podiumRise {
    from { opacity: 0; transform: translateY(36px) scaleY(0.92); transform-origin: bottom; }
    to   { opacity: 1; transform: translateY(0) scaleY(1); }
  }
  @keyframes podiumGlow {
    0%, 100% { opacity: 0.35; transform: scale(1); }
    50%      { opacity: 0.6;  transform: scale(1.08); }
  }
  @keyframes podiumHeaderIn {
    from { opacity: 0; transform: translateY(-10px); }
    to   { opacity: 1; transform: translateY(0); }
  }
`;

const STAND_HEIGHT = { 1: 176, 2: 128, 3: 96 };
const RANK_ORDER = [2, 1, 3]; // visual left-to-right order
const RANK_DELAY = { 3: '0.05s', 2: '0.22s', 1: '0.4s' };

function PodiumBlock({ rank, rows }) {
  if (!rows || !rows.length) return null;
  const colour = PODIUM_COLOURS[rank - 1];
  const manyNames = rows.length > 2;

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      width: 200,
      animation: `podiumRise 0.6s cubic-bezier(0.16, 1, 0.3, 1) both`,
      animationDelay: RANK_DELAY[rank],
    }}>
      {/* Medal */}
      <div style={{
        width: 52, height: 52, borderRadius: '50%',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: `radial-gradient(circle at 32% 28%, ${colour}, ${colour}cc 60%, ${colour}88 100%)`,
        boxShadow: `0 0 0 1px ${colour}55, 0 4px 18px -2px ${colour}66`,
        marginBottom: 12, flexShrink: 0,
      }}>
        <span style={{
          fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 24,
          color: '#0f0f0f', letterSpacing: '-0.02em',
        }}>
          {rank}
        </span>
      </div>

      {/* Name(s) */}
      <div style={{
        textAlign: 'center', marginBottom: 6,
        display: 'flex', flexDirection: 'column', gap: 2,
        minHeight: 26,
      }}>
        {rows.map(r => (
          <span key={r.user_name} style={{
            fontFamily: 'var(--font-display)', fontWeight: 700,
            fontSize: manyNames ? 17 : 21, lineHeight: 1.05,
            color: '#f2f2f2', textTransform: 'uppercase', letterSpacing: '0.01em',
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 190,
          }}>
            {r.user_name}
          </span>
        ))}
      </div>

      {/* Score */}
      <div style={{
        fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: 15,
        color: colour, marginBottom: 14, letterSpacing: '0.02em',
      }}>
        {rows[0].score} <span style={{ fontSize: 10, color: '#666', fontWeight: 600, letterSpacing: '0.12em' }}>PTS</span>
      </div>

      {/* Stand */}
      <div style={{
        width: '100%', height: STAND_HEIGHT[rank],
        position: 'relative', borderRadius: '4px 4px 0 0',
        background: `linear-gradient(180deg, ${colour}26 0%, ${colour}0d 55%, ${colour}05 100%)`,
        borderTop: `2px solid ${colour}`,
        boxShadow: `0 0 24px -6px ${colour}55`,
        overflow: 'hidden',
      }}>
        <span style={{
          position: 'absolute', bottom: -18, left: '50%', transform: 'translateX(-50%)',
          fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 96,
          color: colour, opacity: 0.09, lineHeight: 1, userSelect: 'none',
        }}>
          {rank}
        </span>
        {rank === 1 && (
          <span style={{
            position: 'absolute', top: 0, left: 0, right: 0, height: '100%',
            background: `radial-gradient(circle at 50% 0%, ${colour}30, transparent 65%)`,
            animation: 'podiumGlow 2.6s ease-in-out infinite',
          }} />
        )}
      </div>
    </div>
  );
}

export default function RacePodium({ race, leaderboard, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const groups = topThreeGroups(leaderboard || []);
  const hasAny = RANK_ORDER.some(r => groups[r]?.length);

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'radial-gradient(ellipse at 50% 30%, rgba(30,20,0,0.55) 0%, rgba(0,0,0,0.94) 65%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <style>{kf}</style>

      {/* Close button */}
      <button
        onClick={onClose}
        style={{
          position: 'absolute', top: 16, right: 20,
          background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)',
          borderRadius: 6, color: '#ccc', fontSize: 22, lineHeight: 1,
          width: 44, height: 44, cursor: 'pointer', display: 'flex',
          alignItems: 'center', justifyContent: 'center',
          fontFamily: 'monospace',
        }}
        aria-label="Close"
      >
        ×
      </button>

      {/* Content — stop click propagation so clicking inside doesn't close */}
      <div
        onClick={e => e.stopPropagation()}
        style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          padding: '0 24px', maxWidth: 780, width: '100%',
        }}
      >
        {/* Header */}
        <div style={{
          textAlign: 'center', marginBottom: 40,
          animation: 'podiumHeaderIn 0.5s ease both',
        }}>
          <div style={{
            fontFamily: 'var(--font-mono)', fontSize: 11, color: '#e8002d',
            letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: 8,
          }}>
            {race.season ? `${race.season} · ` : ''}Round {race.round} · Race Podium
          </div>
          <div style={{
            fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 36,
            color: '#fff', textTransform: 'uppercase', letterSpacing: '0.01em',
            lineHeight: 1.05,
          }}>
            {race.name}
          </div>
          <div style={{
            width: 64, height: 3, margin: '14px auto 0',
            background: 'linear-gradient(90deg, #f9b000, #e8002d)',
            borderRadius: 2,
          }} />
        </div>

        {/* Podium */}
        {hasAny ? (
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 20, justifyContent: 'center' }}>
            {RANK_ORDER.filter(r => groups[r]?.length).map(rank => (
              <PodiumBlock key={rank} rank={rank} rows={groups[rank]} />
            ))}
          </div>
        ) : (
          <p style={{ fontFamily: 'var(--font-mono)', fontSize: 13, color: '#555' }}>No scores yet.</p>
        )}
      </div>

      <p style={{
        position: 'absolute', bottom: 14,
        fontFamily: 'monospace', fontSize: 11, color: '#333',
        letterSpacing: '0.08em',
      }}>
        ESC TO CLOSE
      </p>
    </div>
  );
}
